import React, { useEffect, useRef, useState } from 'react';
import Modal from '@/components/Modal';
import Text from '@/components/Text';
import Button, { ButtonType } from '@/components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-thin-svg-icons';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Image from 'next/image';
import PhoneService from '@/lib/Phone';
import PhoneUtils from '@/utils/PhoneUtils';
import useCurrentUser from '@/hooks/useCurrentUser';
import Analytics from '@/system/Analytics';
import { YourTypeEvents } from '@/system/Analytics/events/YourTypeEvents';

interface YourTypeGetAppModalProps {
  open: boolean;
  closeModal: () => void;
  source?: string;
}

const IOS_APP_URL = 'https://apps.apple.com/us/app/bloom-learn-to-invest/id1576588253';
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.joinbloom.bloomapp&pcampaignid=web_share';

type ModalStep = 'initial' | 'success';

export default function YourTypeGetAppModal({ open, closeModal, source = 'unknown' }: YourTypeGetAppModalProps) {
  const { data: user } = useCurrentUser();
  const [step, setStep] = useState<ModalStep>('initial');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasTrackedAcquiredRef = useRef(false);

  useEffect(() => {
    if (open) {
      hasTrackedAcquiredRef.current = false;
      if (user?.account?.phoneNumber) {
        setPhoneNumber(PhoneUtils.formatIncompletePhone(user.account.phoneNumber));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trackAppAcquired = (method: 'download_click' | 'text_link_success', platform?: 'ios' | 'android') => {
    if (hasTrackedAcquiredRef.current) return;
    hasTrackedAcquiredRef.current = true;
    Analytics.track(YourTypeEvents.GET_APP_ACQUIRED, {
      source,
      method,
      ...(platform ? { platform } : {}),
    });
  };

  const onDownload = (platform: 'ios' | 'android') => {
    const url = platform === 'ios' ? IOS_APP_URL : ANDROID_APP_URL;
    if (!url) return;
    Analytics.track(YourTypeEvents.GET_APP_DOWNLOAD_CLICK, {
      source,
      platform,
      url,
    });
    trackAppAcquired('download_click', platform);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = PhoneUtils.formatIncompletePhone(e.target.value);
    setPhoneNumber(formatted);
    if (error) setError(null);
  };

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneNumber.length < 10) return;
    Analytics.track(YourTypeEvents.GET_APP_TEXT_LINK_ATTEMPT, {
      source,
      phoneLength: phoneNumber.replace(/\D/g, '').length,
    });
    setIsLoading(true);
    setError(null);
    try {
      await PhoneService.sendDownloadLink(phoneNumber);
      Analytics.track(YourTypeEvents.GET_APP_TEXT_LINK_SUCCESS, {
        source,
      });
      trackAppAcquired('text_link_success');
      setStep('success');
    } catch (err) {
      Analytics.track(YourTypeEvents.GET_APP_TEXT_LINK_ERROR, {
        source,
        error: err instanceof Error ? err.message : 'unknown_error',
      });
      setError('Failed to send link. Please check your number.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setStep('initial');
    setPhoneNumber('');
    setError(null);
  };

  const handleClose = () => {
    closeModal();
    setTimeout(resetModal, 300);
  };

  return (
    <Modal open={open} closeModal={handleClose}>
      <div className="w-full max-w-lg overflow-hidden flex flex-col shadow-2xl relative bg-gradient-blue rounded-2xl text-white">
        {/* Close button */}
        <button
          aria-label="Close"
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 size-8 rounded-full bg-white/10 flex items-center justify-center text-white/50 hover:bg-white/20 hover:text-white transition-all"
        >
          <span className="text-lg leading-none">&times;</span>
        </button>

        <div className="flex flex-col items-center px-8 pt-8 pb-10 gap-4">
          {/* Bloom Icon */}
          <div className="size-24 rounded-[27px] overflow-hidden shrink-0">
            <Image src="/images/bloom.svg" width={96} height={96} alt="Bloom" />
          </div>

          {step === 'initial' ? (
            <>
              {/* Title & Subtitle */}
              <div className="flex flex-col items-center text-center max-w-[480px] w-full">
                <h2 className="font-game leading-8 text-2xl text-white uppercase w-full">
                  JOIN 1.5M+ INVESTORS ON BLOOM
                </h2>
                <p className="text-[17px] leading-7 font-light text-white/50 w-full">
                  Download the app and open your investing account to start building your automated portfolio.
                </p>
              </div>

              {/* App Store Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 items-center max-w-[480px] w-full">
                <button
                  onClick={() => onDownload('ios')}
                  className="flex-1 flex items-center justify-center gap-2.5 bg-white h-14 rounded-full hover:bg-white/90 transition-colors w-full"
                >
                  <FontAwesomeIcon icon={faApple as IconProp} className="text-2xl text-black" />
                  <div className="flex flex-col items-start leading-tight text-left">
                    <span className="text-[9px] text-black/60 whitespace-nowrap">Download on the</span>
                    <span className="text-base font-bold text-black whitespace-nowrap">App Store</span>
                  </div>
                </button>
                <button
                  onClick={() => onDownload('android')}
                  className="flex-1 flex items-center justify-center gap-2.5 bg-white h-14 rounded-full hover:bg-white/90 transition-colors w-full"
                >
                  <FontAwesomeIcon icon={faGooglePlay as IconProp} className="text-lg text-black" />
                  <div className="flex flex-col items-start leading-tight text-left">
                    <span className="text-[9px] uppercase text-black/60 whitespace-nowrap">Get it on</span>
                    <span className="text-base font-bold text-black whitespace-nowrap">Google Play</span>
                  </div>
                </button>
              </div>

              {/* "or" */}
              <p className="text-lg leading-6 text-white text-center w-full">or</p>

              {/* Phone Input + Send Link */}
              <form onSubmit={handleSendLink} className="flex gap-4 items-start max-w-[480px] w-full">
                <div className="relative flex-1">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="(555) 555-5555"
                    className="w-full h-14 border border-white/30 rounded-full px-5 text-white text-lg text-center placeholder:text-white/30 focus:outline-none focus:border-white/50 transition-colors"
                    style={{ background: 'linear-gradient(106deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.025) 100%)' }}
                  />
                  {isLoading && (
                    <div className="absolute right-5 top-1/2 -translate-y-1/2">
                      <div className="size-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <Button
                  disabled={phoneNumber.length < 10 || isLoading}
                  type={ButtonType.Primary}
                  size="md"
                  className="h-14 px-8 whitespace-nowrap"
                >
                  Send link
                </Button>
              </form>
              {error && <Text size="xs" className="text-red-400">{error}</Text>}
            </>
          ) : (
            /* Success Step */
            <div className="flex flex-col items-center gap-4 py-4 text-center max-w-[480px] w-full">
              <FontAwesomeIcon icon={faCheckCircle as IconProp} className="text-4xl text-green-400" />
              <div className="flex flex-col gap-1">
                <h2 className="font-game leading-8 text-2xl text-white uppercase">
                  Link Sent!
                </h2>
                <p className="text-lg text-white/50">
                  Check your phone to download Bloom.
                </p>
              </div>
              <Button
                type={ButtonType.Outlined}
                size="md"
                className="mt-2"
                onClick={handleClose}
              >
                Got it
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
